import { ObservabilityLogLevel } from "@inditex/core-o11y-js";
import { sendLog } from "src/utils/observability/observabilityUtils";

const PRODUCT_CREATION_LABEL = "Product Creation";
const IMAGE_UPLOAD_LABEL = "Image Upload Error";
const PRODUCT_CREATION_ERROR_LABEL = "Product Creation Error";
const PRODUCT_IMAGE_TRACE_LABEL = "Product Image Trace";

type ProductImageTraceItem = {
  assetId?: string;
  deliveryUrl?: string;
  indexFile?: number;
};

function extractHost(url?: string): string {
  if (!url) return "without_url";

  try {
    return new URL(url).host;
  } catch {
    return "invalid_url";
  }
}

function extractPathname(url?: string): string {
  if (!url) return "without_url";

  try {
    return new URL(url).pathname;
  } catch {
    return "invalid_url";
  }
}

export const logImageUploadError = (errorType: string): void => {
  sendLog({
    message: `Image upload failed: ${errorType}`,
    level: ObservabilityLogLevel.ERROR,
    label: IMAGE_UPLOAD_LABEL,
    tags: { error_type: errorType },
  });
};

export const logProductCreationStarted = (imageCount: number): void => {
  sendLog({
    message: "Product creation request sent",
    label: PRODUCT_CREATION_LABEL,
    tags: { image_count: String(imageCount) },
  });
};

export const logProductCreationSuccess = (): void => {
  sendLog({
    message: "Product created successfully",
    label: PRODUCT_CREATION_LABEL,
    tags: { created: "true" },
  });
};

export const logProductCreationApiError = (): void => {
  sendLog({
    message: "Product creation API call failed",
    level: ObservabilityLogLevel.ERROR,
    label: PRODUCT_CREATION_ERROR_LABEL,
    tags: { created: "false" },
  });
};

export const logProductCreationRejected = (): void => {
  sendLog({
    message: "Product creation rejected by server",
    level: ObservabilityLogLevel.ERROR,
    label: PRODUCT_CREATION_ERROR_LABEL,
    tags: { created: "false" },
  });
};

function serializeHeaders(headers: Record<string, unknown> | undefined): string {
  if (!headers) return "no_headers";
  return Object.entries(headers)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join("; ");
}

export interface ProductCreationRequestDetails {
  operation: "create" | "update";
  storeId: string | number;
  lang: string;
  imageCount: number;
  categoryId?: number;
  catalogId?: number;
  sizeId?: number;
  condition?: string | number;
}

export const logProductCreationRequest = (
  details: ProductCreationRequestDetails,
): void => {
  sendLog({
    message: "Product creation API request body",
    label: PRODUCT_CREATION_LABEL,
    tags: {
      stage: "product_creation_request",
      operation: details.operation,
      store_id: String(details.storeId),
      lang: details.lang,
      image_count: String(details.imageCount),
      ...(details.categoryId !== undefined
        ? { category_id: String(details.categoryId) }
        : {}),
      ...(details.catalogId !== undefined
        ? { catalog_id: String(details.catalogId) }
        : {}),
      ...(details.sizeId !== undefined
        ? { size_id: String(details.sizeId) }
        : {}),
      ...(details.condition ? { condition: String(details.condition) } : {}),
    },
  });
};

export const logProductCreationApiSuccess = (
  response: {
    status?: number;
    headers?: Record<string, unknown>;
    data?: { id?: number | string };
  },
  operation: "create" | "update",
): void => {
  let responseStr = "[unable to serialize]";
  try {
    responseStr = JSON.stringify(response);
  } catch (_) {}
  sendLog({
    message: "Product creation API response success",
    label: PRODUCT_CREATION_LABEL,
    tags: {
      stage: "product_creation_api_success",
      operation,
      response_status: String(response?.status ?? "unknown"),
      product_id: String(response?.data?.id ?? "unknown"),
      headers: serializeHeaders(response?.headers),
      response: responseStr,
    },
  });
};

export const logProductCreationApiFailure = (
  error: unknown,
  operation: "create" | "update",
): void => {
  const axiosResponse = (error as any)?.response;
  let errorStr = "[unable to serialize]";
  try {
    errorStr = JSON.stringify(axiosResponse ?? error);
  } catch (_) {
    errorStr = String(error);
  }
  sendLog({
    message: "Product creation API response failure",
    level: ObservabilityLogLevel.ERROR,
    label: PRODUCT_CREATION_ERROR_LABEL,
    tags: {
      stage: "product_creation_api_failure",
      operation,
      response_status: String(axiosResponse?.status ?? "unknown"),
      error_message: String((error as any)?.message ?? "unknown"),
      headers: serializeHeaders(axiosResponse?.headers),
      response: errorStr,
    },
  });
};

export const logProductImagesTrace = (
  stage: string,
  images: ProductImageTraceItem[],
  operation: "create" | "update",
): void => {
  images.forEach((image, position) => {
    sendLog({
      message: `Product images trace: ${stage}`,
      label: PRODUCT_IMAGE_TRACE_LABEL,
      tags: {
        stage,
        operation,
        position: String(position),
        is_first_image: String(position === 0),
        index_file:
          typeof image?.indexFile === "number"
            ? String(image.indexFile)
            : "without_index_file",
        asset_id: image?.assetId || "without_asset_id",
        delivery_url_host: extractHost(image?.deliveryUrl),
        delivery_url_path: extractPathname(image?.deliveryUrl),
        has_delivery_url: String(Boolean(image?.deliveryUrl)),
      },
    });
  });
};

const AKAMAI_SIZE_LIMIT_BYTES = 10 * 1024 * 1024; // 10MB

export const logOriginalImageUploadRequest = (
  originalFile: File,
  assetId: string,
  index: number,
): void => {
  const fileSizeMb = (originalFile.size / (1024 * 1024)).toFixed(2);
  const exceedsAkamaiLimit = originalFile.size > AKAMAI_SIZE_LIMIT_BYTES;
  sendLog({
    message: exceedsAkamaiLimit
      ? `Original image upload request - WARNING: file ${fileSizeMb}MB exceeds Akamai 10MB limit`
      : "Original image upload request",
    level: exceedsAkamaiLimit
      ? ObservabilityLogLevel.WARN
      : ObservabilityLogLevel.INFO,
    label: PRODUCT_IMAGE_TRACE_LABEL,
    tags: {
      stage: "original_image_upload_request",
      asset_id: assetId,
      index: String(index),
      file_name: originalFile.name,
      file_size: String(originalFile.size),
      file_size_mb: fileSizeMb,
      file_type: originalFile.type || "empty",
      exceeds_akamai_limit: String(exceedsAkamaiLimit),
    },
  });
};

export interface OriginalImageUploadResultDetails {
  responseStatus?: number;
  responseStatusText?: string;
  responseBody?: string;
  responseType?: string;
  akamaiRef?: string;
}

function classifyUploadError(error: unknown, fileSize: number): string {
  const msg = String(error).toLowerCase();
  if (fileSize > AKAMAI_SIZE_LIMIT_BYTES) return "akamai_size_limit";
  if (
    msg.includes("failed to fetch") ||
    msg.includes("load failed") ||
    msg.includes("networkerror")
  )
    return "network_or_cors";
  if ((error as any)?.uploadResponse?.status === 413)
    return "http_413_too_large";
  if ((error as any)?.uploadResponse?.status >= 400) return "http_error";
  return "unknown";
}

export const logOriginalImageUploadResult = (
  originalFile: File,
  assetId: string,
  index: number,
  success: boolean,
  error?: unknown,
  details?: OriginalImageUploadResultDetails,
): void => {
  const errorType = error
    ? classifyUploadError(error, originalFile.size)
    : undefined;
  sendLog({
    message: success
      ? "Original image upload success"
      : `Original image upload failed [${errorType}]`,
    level: success ? ObservabilityLogLevel.INFO : ObservabilityLogLevel.ERROR,
    label: PRODUCT_IMAGE_TRACE_LABEL,
    tags: {
      stage: "original_image_upload_result",
      asset_id: assetId,
      index: String(index),
      file_name: originalFile.name,
      file_size: String(originalFile.size),
      file_size_mb: (originalFile.size / (1024 * 1024)).toFixed(2),
      file_type: originalFile.type || "empty",
      success: String(success),
      ...(errorType ? { error_type: errorType } : {}),
      ...(error ? { error: String(error) } : {}),
      ...(details?.responseStatus !== undefined
        ? { response_status: String(details.responseStatus) }
        : {}),
      ...(details?.responseStatusText
        ? { response_status_text: details.responseStatusText }
        : {}),
      ...(details?.responseBody ? { response_body: details.responseBody } : {}),
      ...(details?.responseType ? { response_type: details.responseType } : {}),
      ...(details?.akamaiRef ? { akamai_ref: details.akamaiRef } : {}),
    },
  });
};

export const logNewImageUploadSuccess = (
  indexFile: number,
  assetId: string,
  deliveryUrl: string,
): void => {
  sendLog({
    message: "New image upload success",
    label: PRODUCT_IMAGE_TRACE_LABEL,
    tags: {
      stage: "new_image_upload_success",
      index_file: String(indexFile),
      asset_id: assetId,
      delivery_url_host: extractHost(deliveryUrl),
      has_delivery_url: String(Boolean(deliveryUrl)),
    },
  });
};

function classifyNewUploadError(error: unknown, fileSize?: number): string {
  const msg = String((error as any)?.message ?? "").toLowerCase();
  const code = (error as any)?.code as string | undefined;
  const status = (error as any)?.response?.status as number | undefined;

  if (code === "ECONNABORTED" || msg.includes("timeout")) return "timeout";
  if (fileSize != null && fileSize > 6 * 1024 * 1024) return "file_size_over_6mb";
  if (status === 413) return "http_413_too_large";
  if (
    msg.includes("failed to fetch") ||
    msg.includes("load failed") ||
    msg.includes("networkerror")
  )
    return "network_or_cors";
  if (code === "ERR_NETWORK") return "network_or_cors";
  if (code === "ERR_CANCELED") return "canceled";
  if (status != null && status >= 400) return `http_${status}`;
  return "unknown";
}

export const logNewImageUploadError = (
  indexFile: number,
  error: unknown,
  file?: File,
): void => {
  const axiosError = error as any;
  const axiosResponse = axiosError?.response;
  const errorType = classifyNewUploadError(error, file?.size);

  sendLog({
    message: `New image upload failed [${errorType}]`,
    level: ObservabilityLogLevel.ERROR,
    label: PRODUCT_IMAGE_TRACE_LABEL,
    tags: {
      stage: "new_image_upload_error",
      index_file: String(indexFile),
      error_type: errorType,
      response_status: String(axiosResponse?.status ?? "unknown"),
      error_message: String(
        axiosError?.message ?? axiosError?.title ?? "unknown",
      ),
      // File metadata
      ...(file ? { file_size_bytes: String(file.size) } : {}),
      ...(file ? { file_size_mb: (file.size / (1024 * 1024)).toFixed(2) } : {}),
      ...(file?.type ? { file_type: file.type } : { file_type: "empty" }),
      ...(file?.name ? { file_name: file.name } : {}),
      // Axios error context
      ...(axiosError?.code ? { error_code: axiosError.code } : {}),
      has_request: String(Boolean(axiosError?.request)),
      has_response: String(Boolean(axiosResponse)),
      // Response details (when backend/proxy did respond)
      ...(axiosResponse?.data?.title
        ? { response_title: String(axiosResponse.data.title) }
        : {}),
      ...(axiosResponse?.headers?.["content-type"]
        ? { response_content_type: axiosResponse.headers["content-type"] }
        : {}),
      // Request URL (confirms final host, detects redirects)
      ...(axiosError?.config?.url
        ? { request_url: axiosError.config.url }
        : {}),
    },
  });
};
